import {chain, concat} from 'lodash';
import React, {Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Renderer, RendererOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {isEnumType} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {Icon, IconName} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis, flexCenter} from '../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../libs/shared/ui/src/theme/userThemeConsumer';
import {AccountAvatar} from '../../components/common/avatars/accountAvatar';
import {TeamAvatar} from '../../components/common/avatars/teamAvatar';
import {TeammateAvatar} from '../../components/common/avatars/teammateAvatar';
import {ColorDot} from '../../components/common/colorDot';
import {renderTagIcon} from '../../components/common/dropdowns/dropdownItemTagIcon';
import {DropdownFetcher} from '../../components/common/dropdowns/source/asyncDropdownSource';
import {getChannelIconName} from '../../helpers/channelViewHelpers';
import {buildTeammateDisplayName, inboxHasColor} from '../../helpers/modelHelpers';
import {
  computeIsGlobalNamespace,
  globalNamespace,
  isTeamNamespace,
} from '../../helpers/models/namespaceHelpers';
import {buildTagSearchUrl, SearchTagStatusesEnum} from '../../helpers/models/tagHelpers';
import {AccountModel} from '../../models/api/accountModel';
import {
  AnalyticsFilterTypesEnum,
  AnalyticsInboxScopeFiltersEnum,
} from '../../models/api/analyticsSavedViewModel';
import {ChannelModel} from '../../models/api/channelModel';
import {CompanyModel} from '../../models/api/companyModel';
import {InboxModel} from '../../models/api/inboxModel';
import {ApiSearchTags, SearchTagModel} from '../../models/api/searchTagModel';
import {TagBehaviorsEnum, TagModel} from '../../models/api/tagModel';
import {TeammateModel} from '../../models/api/teammateModel';
import {TeamModel} from '../../models/api/teamModel';
import {fetchApiAsync} from '../../services/http/http';
import {ResourceMap} from '../../store/appTypes';

/*
 * Style.
 */

const StyledColorDot = styled(ColorDot)`
  margin: 0 7px;
  display: inline-block;
`;

const StyledInlineIcon = styled(Icon)`
  display: inline-block;
  margin: 0 4px;
  vertical-align: middle;
`;

const StyledTagWrapper = styled.div`
  ${flexCenter};
`;

const StyledAccountAvatar = styled(AccountAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
  margin-right: 4px;
`;

const StyledTagTitle = styled.div`
  ${ellipsis()};
`;

const StyledTagIcon = styled.div`
  margin-right: 4px;
`;

const StyledWrapper = styled.div`
  ${flexCenter}
`;

const StyledIconDiv = styled.div<{$shouldShrink?: boolean}>`
  flex-shrink: 0;
  margin-right: ${(p) => (p.$shouldShrink ? '4px' : '8px')};
`;

const StyledValue = styled.div`
  ${ellipsis()};
`;

const StyledTeammateAvatar = styled(TeammateAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
`;

const StyledTeamAvatar = styled(TeamAvatar)`
  display: inline-block;
  vertical-align: middle;
  position: relative;
  bottom: 1px;
`;

/*
 * Constants.
 */

const accountIconSize = 16;
const DEFAULT_ITEMS_SHOWN = 2;

export interface FilterRenderer<T> {
  renderItem: RendererOf<T>;
  renderSpecialValue?: RendererOf<AnalyticsInboxScopeFiltersEnum>;
  renderAll: Renderer;
}

const inboxScopeFilterRenderer: Record<AnalyticsInboxScopeFiltersEnum, ReactNode> = {
  [AnalyticsInboxScopeFiltersEnum.ALL_INBOXES]: <Trans id="analytics-filter-all-inboxes">All inboxes</Trans>,
  [AnalyticsInboxScopeFiltersEnum.ALL_SHARED_INBOXES]: (
    <Trans id="analytics-filter-all-shared-inboxes">All team inboxes</Trans>
  ),
  [AnalyticsInboxScopeFiltersEnum.ALL_PRIVATE_INBOXES]: (
    <Trans id="analytics-filter-all-private-values">All individual inboxes</Trans>
  ),
};

export const filterRenderers: Record<AnalyticsFilterTypesEnum, FilterRenderer<any>> = {
  [AnalyticsFilterTypesEnum.ACCOUNTS]: {
    renderItem: (account: AccountModel) => (
      <Fragment key={account.id}>
        <StyledAccountAvatar account={account} size={accountIconSize} avatarIconSize={accountIconSize} />
        {account.name}
      </Fragment>
    ),
    renderAll: () => <Trans id="analytics-filters-all-accounts">All accounts</Trans>,
  },
  [AnalyticsFilterTypesEnum.INBOXES]: {
    renderItem: (inbox: InboxModel) => (
      <UserThemeConsumer>
        {(theme) => (
          <Fragment key={inbox.id}>
            <StyledColorDot color={inboxHasColor(inbox) ? inbox.color : theme.greys.shade60} size={8} />
            {inbox.name}
          </Fragment>
        )}
      </UserThemeConsumer>
    ),
    renderSpecialValue: (value: AnalyticsInboxScopeFiltersEnum) => (
      <Fragment key={value}>{inboxScopeFilterRenderer[value]}</Fragment>
    ),
    renderAll: () => <Trans id="analytics-filters-all-inboxes">All team inboxes</Trans>,
  },
  [AnalyticsFilterTypesEnum.FULL_TAGS]: {
    renderItem: (tag: TagModel) => (
      <StyledTagWrapper key={tag.name}>
        <StyledTagIcon>{renderTagIcon(tag, false)}</StyledTagIcon>
        <StyledTagTitle>{tag.name}</StyledTagTitle>
      </StyledTagWrapper>
    ),
    renderAll: () => <Trans id="analytics-filters-all-tags">Select tags</Trans>,
  },
  [AnalyticsFilterTypesEnum.TAGS]: {
    renderItem: (tagName: string) => <Fragment key={tagName}>{tagName}</Fragment>,
    renderAll: () => <Trans id="analytics-filters-all-tags">Select tags</Trans>,
  },
  [AnalyticsFilterTypesEnum.EXCLUDE_TAGS]: {
    renderItem: (tagName: string) => <Fragment key={tagName}>{tagName}</Fragment>,
    renderAll: () => <Trans id="analytics-filters-exclude-no-tags">Exclude tags</Trans>,
  },
  [AnalyticsFilterTypesEnum.WITHOUT_TAGS]: {
    renderItem: () => null,
    renderAll: () => <Trans id="analytics-filters-without-tags">Without tags</Trans>,
  },
  [AnalyticsFilterTypesEnum.CHANNELS]: {
    renderItem: (channel: ChannelModel) => (
      <UserThemeConsumer>
        {(theme) => (
          <Fragment key={channel.id}>
            <StyledInlineIcon name={getChannelIconName(channel.contactSource)} color={theme.greys.shade50} />
            {channel.displayName}
          </Fragment>
        )}
      </UserThemeConsumer>
    ),
    renderAll: () => <Trans id="analytics-filters-all-channels">All channels</Trans>,
  },
  [AnalyticsFilterTypesEnum.TEAMMATES]: {
    renderItem: (teammate: TeammateModel) => (
      <Fragment key={teammate.id}>
        <StyledTeammateAvatar teammate={teammate} size={VisualSizesEnum.SMALL} />{' '}
        {buildTeammateDisplayName(teammate)}
      </Fragment>
    ),
    renderAll: () => <Trans id="analytics-filters-all-teammates">All teammates</Trans>,
  },
  [AnalyticsFilterTypesEnum.TEAMS]: {
    renderItem: (team: TeamModel) => (
      <Fragment key={team.id}>
        <StyledTeamAvatar team={team} size={VisualSizesEnum.SMALL} /> {team.name}
      </Fragment>
    ),
    renderAll: () => <Trans id="analytics-filters-all-teams">All teams</Trans>,
  },
};

/*
 * Helpers.
 */

type RenderFilterIdsType<T> = {
  ids: ReadonlyArray<keyof ResourceMap<T>> | undefined;
  itemsMap: ResourceMap<T>;
  type: AnalyticsFilterTypesEnum;
  iconName?: IconName;
  shouldUseAll?: boolean;
  shouldShrink?: boolean;
  maxItemsCount?: number;
};

export function renderFilterIds<T>({
  ids = [],
  itemsMap,
  type,
  iconName,
  shouldUseAll = true,
  shouldShrink = false,
  maxItemsCount = DEFAULT_ITEMS_SHOWN,
}: RenderFilterIdsType<T>) {
  const {renderItem, renderSpecialValue, renderAll} = filterRenderers[type];

  // We can't just take the first "maxItemsCount" ids since we're not sure they are in itemsMap.
  // Let's find the first "maxItemsCount" items that we have data on in itemsMap.
  const itemsToShow: Array<ReactNode> = [];
  let i = 0;

  while (itemsToShow.length < maxItemsCount && i <= ids.length) {
    const id = ids[i];
    if (isEnumType(id, AnalyticsInboxScopeFiltersEnum) && renderSpecialValue) {
      itemsToShow.push(renderSpecialValue(id));
    } else {
      const item = itemsMap[id];
      if (item) {
        itemsToShow.push(renderItem(item));
      }
    }
    i++;
  }

  const itemsLeft = ids.length - itemsToShow.length;

  /* eslint-disable react/no-array-index-key */
  const filteredValue = (
    <>
      {itemsToShow.map((item, index) => (
        <Fragment key={index}>
          {index > 0 && ', '}
          {item}
        </Fragment>
      ))}
      {maybeDisplayItemsRemaining(itemsLeft, itemsToShow.length)}
    </>
  );
  /* eslint-enable react/no-array-index-key */

  const displayedValue = shouldUseAll && itemsToShow.length === 0 ? renderAll() : filteredValue;

  return renderFilterContent(displayedValue, iconName, shouldShrink);
}

type RenderFilterIdsV2Type<T> = {
  selectedIds: ReadonlyArray<keyof ResourceMap<T>>;
  itemsMap: ResourceMap<T>;
  renderItem: RendererOf<T>;
  iconName?: IconName;
  shouldShrink?: boolean;
  maxItemsCount?: number;
};

export function renderFilterIdsV2<T>({
  selectedIds,
  itemsMap,
  renderItem,
  maxItemsCount = DEFAULT_ITEMS_SHOWN,
}: RenderFilterIdsV2Type<T>): ReactNode {
  // We can't just take the first "maxItemsCount" ids since we're not sure they are in itemsMap.
  // Instead, find the first "maxItemsCount" items that we have data on in itemsMap.
  const itemsToShow: Array<ReactNode> = [];
  let i = 0;

  while (itemsToShow.length < maxItemsCount && i <= selectedIds.length) {
    const id = selectedIds[i];
    const item = itemsMap[id];
    if (item) {
      itemsToShow.push(renderItem(item));
    }
    i++;
  }

  const itemsLeft = selectedIds.length - itemsToShow.length;

  const filteredValue = (
    <>
      {itemsToShow.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {index > 0 && ', '}
          {item}
        </Fragment>
      ))}
      {maybeDisplayItemsRemaining(itemsLeft, itemsToShow.length)}
    </>
  );

  return filteredValue;
}

const maybeDisplayItemsRemaining = (itemsLeft: number, itemsToShowLength: number) => {
  if (itemsLeft < 1) {
    return null;
  }

  return itemsToShowLength ? `, + ${itemsLeft}` : `+ ${itemsLeft}`;
};

export const renderFilterContent = (
  displayedValue: ReactNode,
  iconName: IconName | undefined,
  shouldShrink: boolean,
  iconColor?: string,
) => (
  <UserThemeConsumer>
    {(theme) => (
      <StyledWrapper>
        {iconName && (
          <StyledIconDiv $shouldShrink={shouldShrink}>
            <Icon name={iconName} color={iconColor ?? theme.greys.shade70} />
          </StyledIconDiv>
        )}
        <StyledValue>{displayedValue}</StyledValue>
      </StyledWrapper>
    )}
  </UserThemeConsumer>
);

export function makeFilterRows<T>(accessor: (item: T) => string) {
  return (items: ReadonlyArray<T>, query: string) => {
    // Shortcut for empty query.
    if (!query) {
      return items;
    }

    // Otherwise, filter with provided function.
    const lowerCaseQuery = query.toLowerCase();
    return chain(items)
      .compact() // Remove the fixed rows.
      .filter((i) => accessor(i).toLowerCase().includes(lowerCaseQuery))
      .value();
  };
}

/*
 * Tags fetcher.
 */

type TagsFetcherMaker = (
  company: CompanyModel,
  namespace: string,
  selectedTeamNamespaces: Set<string>,
) => DropdownFetcher<SearchTagModel>;

export const makeTagsFetcher: TagsFetcherMaker =
  (company: CompanyModel, namespace: string, selectedTeamNamespaces: Set<string>) =>
  async (query, tags, cancelToken) => {
    const behaviors = [TagBehaviorsEnum.CAN_ANALYTICS];

    // Exclude initial items to compute the offset.
    const tagCount = tags.filter((t) => t).length;

    const namespaces = computeTagsFetcherNamespaces(namespace, selectedTeamNamespaces);

    const url = buildTagSearchUrl(company, query, {
      namespaces,
      offset: tagCount,
      behaviors,
      status: SearchTagStatusesEnum.ALL,
    });

    const tagSearch = await fetchApiAsync(ApiSearchTags, url, {cancelToken});

    return {
      items: concat(tags, tagSearch.tags),
      canLoadMore: tagSearch.tags.length === tagSearch.limit,
    };
  };

function computeTagsFetcherNamespaces(
  namespace: string,
  selectedTeamNamespaces: Set<string>,
): ReadonlyArray<string> | undefined {
  if (computeIsGlobalNamespace(namespace)) {
    return [globalNamespace, ...selectedTeamNamespaces];
  }

  if (isTeamNamespace(namespace)) {
    return [globalNamespace, namespace];
  }

  // No FE restriction for an individual report, the BE will return all accessible tags.
  return undefined;
}
